import * as React from "react"
import { useRecoilValue } from "recoil"
import { Box } from "@chakra-ui/layout"
import { findSideRatio } from "../../util/findSideRatio"
import { useResizeObserver } from "../../hooks/useResizeObserver"
import { usersListSmallWindowSelector } from "../../recoil/atoms/userListSmallWindow"
import { badgeHistoryListSelector } from "../../recoil/atoms/badgeHistory"
import { useLocalUserType } from "../../hooks/useLocalUserType"
import useLocalUserHasPermissions from "../../hooks/useLocalUserHasPermissions"
import { PrevSpeakerStreams } from "./_prevSpeakerStreams"
import { MobilePrevSpeakerStreams } from "./small-view/mobile-prev-speaker-steams"
import { LocalUserProperties } from "../../types/localUserProps"
import { brandingSelector } from "../../recoil/atoms/room"
import { activeSpeakerSelector } from "../../recoil/atoms/activeSpeaker"
import { BadgesLiveMode } from "./badgesLiveMode"
import { TableServicePanel } from "../servicesPanel/table-service-panel"
import { shoppableMNPVideoAtom } from "../../recoil/atoms/shoppableVideos"
import { taggedBadgesListSelector } from "../../recoil/atoms/taggedBadges"

interface InteractivePanelProps {
  isTablet: boolean
  widthMainScreen: number
  isPrevSpeakerStreams: boolean
  widthMainScreenContainer: number
  maxHightBadgesListLive: number
  localUserProperties: LocalUserProperties
  isSmallMainScreen: boolean
  heightMainScreenContainer: number
  isHiddenControls: boolean
}

export interface PrevSpeakerStyles {
  highlighterColor: string
  alertBackgroundColor: string
}

const _InteractivePanel: React.FC<InteractivePanelProps> = ({
  isTablet,
  widthMainScreen,
  isPrevSpeakerStreams,
  widthMainScreenContainer,
  maxHightBadgesListLive,
  localUserProperties,
  isSmallMainScreen,
  heightMainScreenContainer,
  isHiddenControls
}) => {
  const userIdsSmallWindows = useRecoilValue(usersListSmallWindowSelector)
  const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const activeSpeaker = useRecoilValue(activeSpeakerSelector)

  const smallScreensRef = React.useRef<HTMLDivElement>(null)

  const [, heightPrevSpeaker] = useResizeObserver(smallScreensRef)
  const { isAudience, isModerator } = useLocalUserType()
  const { canSeeHistory } = useLocalUserHasPermissions()
  const { enabledShoppableMNP } = useRecoilValue(shoppableMNPVideoAtom)
  const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)

  const desktopBadgesHistory = React.useMemo(() => {
    return enabledShoppableMNP ? taggedBadgesList : allBadgeHistory
  }, [allBadgeHistory, enabledShoppableMNP, taggedBadgesList])

  const showBadgesListLive = React.useMemo(() => {
    if (
      desktopBadgesHistory.length > 0 &&
      !isModerator &&
      isHiddenControls &&
      !isPrevSpeakerStreams &&
      canSeeHistory
    ) {
      return true
    }
    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    desktopBadgesHistory.length,
    isModerator,
    isHiddenControls,
    isPrevSpeakerStreams,
    canSeeHistory,
    enabledShoppableMNP
  ])

  const widthContainer = React.useMemo<number>(() => {
    const gapSize = isTablet ? 10 : 16
    return (
      findSideRatio(heightPrevSpeaker, 16, 9) * userIdsSmallWindows.length +
      gapSize * userIdsSmallWindows.length -
      gapSize
    )
  }, [heightPrevSpeaker, userIdsSmallWindows.length, isTablet])

  const maxHeighPrevSpeakerStreams = React.useMemo(() => {
    const gapSize = isTablet ? 10 : 16
    if ((!isAudience || isTablet) && userIdsSmallWindows.length === 0) {
      return "0px"
    } else {
      return userIdsSmallWindows.length === 3
        ? `calc(23% - ${gapSize}px)`
        : userIdsSmallWindows.length >= 4
        ? `calc(21% - ${gapSize}px)`
        : `calc(30% - ${gapSize}px)`
    }
  }, [isAudience, isTablet, userIdsSmallWindows.length])

  const maxHeightContainer = React.useMemo(() => {
    if (isTablet) {
      return maxHeighPrevSpeakerStreams
    }
    return showBadgesListLive
      ? widthMainScreenContainer < 430
        ? maxHightBadgesListLive
        : 123
      : maxHeighPrevSpeakerStreams
  }, [
    isTablet,
    maxHeighPrevSpeakerStreams,
    maxHightBadgesListLive,
    showBadgesListLive,
    widthMainScreenContainer
  ])

  const maxHeightSmallScreen = React.useMemo(() => {
    const maxHeight =
      (((widthMainScreen - (userIdsSmallWindows.length * 16 - 16)) /
        userIdsSmallWindows.length) *
        9) /
      16

    if (maxHeight < heightPrevSpeaker) {
      return maxHeight
    }
    return heightPrevSpeaker
  }, [heightPrevSpeaker, userIdsSmallWindows.length, widthMainScreen])

  const roomStylesPrevSpeaker = React.useMemo<PrevSpeakerStyles>(() => {
    return {
      highlighterColor: roomStyles?.highlighterColor,
      alertBackgroundColor: roomStyles?.alertBackgroundColor
    }
  }, [roomStyles?.highlighterColor, roomStyles.alertBackgroundColor])

  const smallScreenWidth = React.useMemo(
    () => findSideRatio(heightPrevSpeaker, 16, 9) - 10,
    [heightPrevSpeaker]
  )

  const smallScreenStyling: React.CSSProperties = React.useMemo(() => {
    return {
      /* height: "100%", */
      width: smallScreenWidth,
      maxHeight: maxHeightSmallScreen,
      backgroundColor: roomStyles?.alertBackgroundColor ?? "black"
    }
  }, [maxHeightSmallScreen, roomStyles?.alertBackgroundColor, smallScreenWidth])

  const isMainScreen = React.useMemo<boolean>(
    () => localUserProperties.id === activeSpeaker?.uid,
    [activeSpeaker?.uid, localUserProperties.id]
  )

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow={isTablet && "hidden"}
        height={isTablet && "100%"}
        width={widthContainer}
        maxW={isTablet && widthMainScreen}
        ref={smallScreensRef}
        maxHeight={maxHeightContainer}
        pos={isTablet ? "relative" : "unset"}
        minH={userIdsSmallWindows.length > 0 && "50px"}
        margin={
          isTablet && userIdsSmallWindows.length > 0 && "0 auto 10px auto"
        }
        alignItems={!isTablet && "center"}
      >
        {!isTablet && isPrevSpeakerStreams && (
          <PrevSpeakerStreams
            maxWidthScreen={widthMainScreen}
            heightPrevSpeaker={maxHeightSmallScreen}
            localUserProperties={localUserProperties}
            userIdsSmallWindows={userIdsSmallWindows}
            roomStylesPrevSpeaker={roomStylesPrevSpeaker}
          />
        )}
        {isTablet && (
          <MobilePrevSpeakerStreams
            otherUserIds={userIdsSmallWindows}
            isTablet={true}
            isMainScreen={isMainScreen}
            smallScreenStyling={smallScreenStyling}
            localUserProperties={localUserProperties}
          />
        )}
        {!isTablet && showBadgesListLive && (
          <BadgesLiveMode
            width={widthMainScreenContainer}
            isSmallMainScreen={isSmallMainScreen}
          />
        )}
      </Box>
      {isTablet && (
        <TableServicePanel
          heightPrevSpeaker={heightPrevSpeaker}
          localUserProperties={localUserProperties}
          heightMainScreenContainer={heightMainScreenContainer}
          widthMainScreen={widthMainScreen}
        />
      )}
    </>
  )
}

export const InteractivePanel = React.memo(_InteractivePanel)
